// Test file for quickly trying out TriggerEditor with predefined data. Ideally we want it in Storybook, which, however, is fairly difficult to setup.
import { TriggerEditorFrontend } from "../src/components/TriggerEditor/TriggerEditor";
import { trigger as trigger1 } from "../../../prisma/configs/dots-family/test-onboard-private-channel";
import { trigger as trigger2 } from "../../../prisma/configs/dots-family/test-onboard-ephemeral";
import {
  ButtonClickTriggerConfigs,
  TriggerCreate,
  TriggerForEditor,
} from "@dots-projects/types";
import {
  OnPersistTrigger,
  PersistResponse,
} from "../src/components/TriggerEditor/reducer/reducerTypes";
import invariant from "ts-invariant";
import { nanoid } from "nanoid";
import { demoPrivateChannelFlow } from "./demoPrivateChannelTrigger";
import { trigger as arpeggiTrigger } from "./arpeggi-prod";
import { braintrustTestProdTrigger } from "./braintrustFlow1-prod";
import { trigger as mobalyticsTrigger } from "./mobalytics-prod";
import { trigger as demoEphemeralTrigger } from "./demo-ephemeral";
import { trigger as privateChannelTrigger } from "./demo-private-channel";

import { quicknodeProdTrigger } from "./quicknode-prod";

const onPersist: OnPersistTrigger = async (kind, trigger) => {
  await new Promise((resolve) => setTimeout(resolve, 800));
  let overrides = {},
    response: PersistResponse = {};
  switch (kind) {
    case "view-draft":
      invariant(!trigger.isDraft);
      overrides = {
        liveTriggerConfigs: { type: trigger.type, configs: trigger.configs },
        type: "button_click",
      };
      break;
    case "publish":
      invariant(trigger.isDraft);
      overrides = trigger.liveTriggerConfigs;
      break;
    case "try-draft":
      switch (trigger.type) {
        case "button_click":
          overrides = {
            configs: {
              messages: [
                { id: `msg-${nanoid(4)}`, buttonIds: [`btn-${nanoid(4)}`] },
              ],
            },
          } as {
            configs: ButtonClickTriggerConfigs;
          };
          break;
      }
      // uncomment to test error response
      // throw new Error('Network Error')
      response["dotsAdminsChannelUrl"] =
        "https://discord.com/channels/958828554097930241/1014332190198345828";
      break;
  }
  const newTrigger = {
    ...trigger,
    id: `${Math.random()}`,
    updatedAt: new Date(),
    isDraft: kind !== "publish",
    ...overrides,
  } as TriggerForEditor;
  response["triggers"] = [newTrigger];

  console.log(`mock ${kind} trigger`, { newTrigger, response });
  return [newTrigger, response];
};

export function Trigger1() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...trigger1,
            id: "abcd",
            updatedAt: new Date(),
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}
Trigger1.storyName = "Onboard private channel";

export function Trigger2() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...trigger2,
            id: "abcd",
            updatedAt: new Date(),
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function TeamJoinTrigger() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...trigger2,
            id: "abcd",
            updatedAt: new Date(),
            steps: [],
            isDraft: true,
            liveTriggerConfigs: {
              type: "team_join",
            },
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function ButtonClickTrigger() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...trigger2,
            id: "abcd",
            updatedAt: new Date(),
            steps: [],
            isDraft: true,
            liveTriggerConfigs: {
              type: "button_click",
              configs: {
                messages: [{ id: "$todo", buttonIds: ["$todo"] }],
              },
            },
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function MissingPosition() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...trigger2,
            configs: {
              $$editorPosition: { x: 0, y: 0 },
            },
            id: "abcd",
            updatedAt: new Date(),
            steps: [
              {
                type: "send_message",
                name: "Send a message",
                id: "send_message-0-bkmuwS",
                configs: {
                  removeUrlEmbeds: false,
                  channelId: "$channelId",
                  message: "Hello world!",
                },
                transitions: [],
              },
            ],
            isDraft: true,
            liveTriggerConfigs: {
              type: "button_click",
              configs: {
                messages: [{ id: "$todo", buttonIds: ["$todo"] }],
              },
            },
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function RoleChangeTrigger() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={{
          ...trigger2,
          id: "abcd",
          updatedAt: new Date(),
          steps: [],
          isDraft: true,
          liveTriggerConfigs: {
            type: "role_change",
            // TODO: Configs here?
          },
        }}
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function DemoPrivateChannelTrigger() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...demoPrivateChannelFlow,
            updatedAt: new Date(),
            isDraft: true,
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function Arpeggi() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...arpeggiTrigger,
            updatedAt: new Date(),
            isDraft: true,
            liveTriggerConfigs: { type: "button_click" },
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function Braintrust() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...braintrustTestProdTrigger,
            updatedAt: new Date(),
            isDraft: true,
            liveTriggerConfigs: {
              type: "button_click",
            },
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function Mobalytics() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...mobalyticsTrigger,
            updatedAt: new Date(),
            isDraft: true,
            liveTriggerConfigs: {
              type: "button_click",
            },
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function Quicknode() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...quicknodeProdTrigger,
            updatedAt: new Date(),
            isDraft: true,
            liveTriggerConfigs: {
              type: "button_click",
            },
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function DemoEphemeral() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...demoEphemeralTrigger,
            updatedAt: new Date(),
            isDraft: true,
            liveTriggerConfigs: {
              type: "button_click",
            },
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function DemoPrivate() {
  return (
    <div className="h-screen m-5">
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...privateChannelTrigger,
            updatedAt: new Date(),
            isDraft: true,
            liveTriggerConfigs: {
              type: "button_click",
            },
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function DeleteMultiSelectDebug() {
  const steps = [
    {
      type: "send_multiple_select",
      name: "Send a survey (multiple selections)",
      id: "send_multiple_select-0-0vzfFY",
      configs: {
        removeUrlEmbeds: false,
        channelId: "$channelId",
        message: "Where are you from?",
        type: "buttons",
        options: [
          {
            emoji: "🟢",
            id: "na-send_multiple_select-0-0vzfFY",
            label: "North America",
          },
        ],
        $$editorPosition: {
          x: 0,
          y: 180,
        },
        ephemeral: false,
        $$editorHtmlMessage: "<p>Where are you from?</p>",
      },
      transitions: [
        {
          targetStepId: "$$step-if-condition-idle-2-9s4zaz",
          when: {
            userResponse: {
              type: "multiple-select",
              userId: "{{user.id}}",
              messageId: "{{send_multiple_select-0-0vzfFY.messageId}}",
            },
          },
        },
      ],
    },
    {
      id: "$$step-if-condition-idle-2-9s4zaz",
      name: "ifElse2",
      type: "idle",
      if: {
        condition: {
          "send_multiple_select-0-0vzfFY.userResponse.optionIds": {
            array_contains: "na-send_multiple_select-0-0vzfFY",
          },
        },
        elseStepId: "$$step-placeholder-idle-Done-KUjAOi",
      },
      transitions: [
        {
          targetStepId: "$$step-placeholder-5HxqXftransition",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 0,
          y: 340,
        },
      },
    },
    {
      type: "idle",
      id: "$$step-placeholder-idle-Done-KUjAOi",
      transitions: [],
      configs: {
        $$editorPosition: {
          x: 0,
          y: 1200,
        },
      },
    },
    {
      type: "send_message",
      name: "Send a message",
      id: "send_message-0-bkmuwS",
      configs: {
        removeUrlEmbeds: false,
        channelId: "$channelId",
        message: "Hello world!",
        $$editorPosition: {
          x: 72.5,
          y: 1040,
        },
      },
      transitions: [
        {
          targetStepId: "$$step-placeholder-idle-Done-KUjAOi",
        },
      ],
    },
    {
      type: "send_message",
      name: "Send a message",
      id: "send_message-0-ZLfjca",
      configs: {
        removeUrlEmbeds: false,
        channelId: "$channelId",
        message: "Hello world!",
        $$editorPosition: {
          x: 207.5,
          y: 840,
        },
      },
      transitions: [
        {
          targetStepId: "send_message-0-bkmuwS",
        },
      ],
    },
    {
      type: "send_message",
      name: "Send a message",
      id: "send_message-0-pxQak1",
      configs: {
        removeUrlEmbeds: false,
        channelId: "$channelId",
        message: "Hello world!",
        $$editorPosition: {
          x: -62.5,
          y: 840,
        },
      },
      transitions: [
        {
          targetStepId: "send_message-0-bkmuwS",
        },
      ],
    },
    {
      id: "edit_roles-0-_x03I_",
      type: "edit_roles",
      name: "Edit roles",
      configs: {
        userIds: [],
        addRoles: [],
        removeRoles: [],
        ephemeral: false,
        acknowledge: true,
        followUpMessage: "Great, the role has been applied!",
        followUpChannelId: "$channelId",
        $$editorPosition: {
          x: 207.5,
          y: 640,
        },
      },
      transitions: [
        {
          targetStepId: "send_message-0-ZLfjca",
        },
      ],
    },
    {
      id: "$$step-placeholder-5HxqXftransition",
      type: "idle",
      configs: {
        $$editorPosition: {
          x: 72.5,
          y: 500,
        },
      },
      transitions: [
        {
          targetStepId: "send_message-0-pxQak1",
        },
      ],
    },
  ];
  return (
    <div className="h-screen m-5">
      <div className="max-w-sm">
        <p>Bug: An error thrown when trying to add a step in between. </p>
        To reproduce the bug, delete multi-select and then try to add a step
        between edit_roles and send_message
      </div>
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...privateChannelTrigger,
            steps,
            updatedAt: new Date(),
            isDraft: true,
            liveTriggerConfigs: {
              type: "button_click",
            },
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function DeleteMultiSelectInSuccessionDebug() {
  const steps = [
    {
      type: "send_multiple_select",
      name: "Send a survey (multiple selections)",
      id: "send_multiple_select-0-wirQcq",
      configs: {
        removeUrlEmbeds: false,
        channelId: "$channelId",
        message: "Where are you from?",
        type: "buttons",
        options: [
          {
            emoji: "🟡",
            id: "africa-send_multiple_select-0-wirQcq",
            label: "Africa",
          },
          {
            emoji: "🔵",
            id: "asia-send_multiple_select-0-wirQcq",
            label: "Asia",
          },
          {
            emoji: "🟢",
            id: "na-send_multiple_select-0-wirQcq",
            label: "North America",
          },
        ],
        $$editorPosition: {
          x: 0,
          y: 180,
        },
      },
      transitions: [
        {
          targetStepId: "$$step-if-condition-idle-0-BKWukN",
          when: {
            userResponse: {
              type: "multiple-select",
              userId: "{{user.id}}",
              messageId: "{{send_multiple_select-0-wirQcq.messageId}}",
            },
          },
        },
      ],
    },
    {
      id: "$$step-if-condition-idle-0-BKWukN",
      name: "ifElse0",
      type: "idle",
      if: {
        condition: {
          "send_multiple_select-0-wirQcq.userResponse.optionIds": {
            array_contains: "africa-send_multiple_select-0-wirQcq",
          },
        },
        elseStepId: "$$step-if-condition-idle-1-qR9nyG",
      },
      transitions: [
        {
          targetStepId: "$$step-placeholder-idle-0Yes-GCh8JG",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 0,
          y: 340,
        },
      },
    },
    {
      id: "$$step-placeholder-idle-0Yes-GCh8JG",
      name: "ifElseYes0",
      type: "idle",
      transitions: [
        {
          targetStepId: "$$step-if-condition-idle-1-qR9nyG",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 72.5,
          y: 460,
        },
      },
    },
    {
      id: "$$step-if-condition-idle-1-qR9nyG",
      name: "ifElse1",
      type: "idle",
      if: {
        condition: {
          "send_multiple_select-0-wirQcq.userResponse.optionIds": {
            array_contains: "asia-send_multiple_select-0-wirQcq",
          },
        },
        elseStepId: "$$step-if-condition-idle-2-evkcLQ",
      },
      transitions: [
        {
          targetStepId: "$$step-placeholder-idle-1Yes-_hDOAM",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 0,
          y: 580,
        },
      },
    },
    {
      id: "$$step-placeholder-idle-1Yes-_hDOAM",
      name: "ifElseYes1",
      type: "idle",
      transitions: [
        {
          targetStepId: "$$step-if-condition-idle-2-evkcLQ",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 72.5,
          y: 700,
        },
      },
    },
    {
      id: "$$step-if-condition-idle-2-evkcLQ",
      name: "ifElse2",
      type: "idle",
      if: {
        condition: {
          "send_multiple_select-0-wirQcq.userResponse.optionIds": {
            array_contains: "na-send_multiple_select-0-wirQcq",
          },
        },
        elseStepId: "send_multiple_select-0-8HthCF",
      },
      transitions: [
        {
          targetStepId: "$$step-placeholder-idle-2Yes-bcNL0f",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 0,
          y: 820,
        },
      },
    },
    {
      id: "$$step-placeholder-idle-2Yes-bcNL0f",
      name: "ifElseYes2",
      type: "idle",
      transitions: [
        {
          targetStepId: "send_multiple_select-0-8HthCF",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 72.5,
          y: 940,
        },
      },
    },
    {
      type: "send_multiple_select",
      name: "Send a survey (multiple selections)",
      id: "send_multiple_select-0-8HthCF",
      configs: {
        removeUrlEmbeds: false,
        channelId: "$channelId",
        message: "Where are you from?",
        type: "buttons",
        options: [
          {
            emoji: "🟡",
            id: "africa-send_multiple_select-0-8HthCF",
            label: "Africa",
          },
          {
            emoji: "🔵",
            id: "asia-send_multiple_select-0-8HthCF",
            label: "Asia",
          },
          {
            emoji: "🟢",
            id: "na-send_multiple_select-0-8HthCF",
            label: "North America",
          },
        ],
        $$editorPosition: {
          x: 0,
          y: 1100,
        },
      },
      transitions: [
        {
          targetStepId: "$$step-if-condition-idle-0-qWFyyr",
          when: {
            userResponse: {
              type: "multiple-select",
              userId: "{{user.id}}",
              messageId: "{{send_multiple_select-0-8HthCF.messageId}}",
            },
          },
        },
      ],
    },
    {
      id: "$$step-if-condition-idle-0-qWFyyr",
      name: "ifElse0",
      type: "idle",
      if: {
        condition: {
          "send_multiple_select-0-8HthCF.userResponse.optionIds": {
            array_contains: "africa-send_multiple_select-0-8HthCF",
          },
        },
        elseStepId: "$$step-if-condition-idle-1-EumTgn",
      },
      transitions: [
        {
          targetStepId: "$$step-placeholder-idle-0Yes-5khqpQ",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 0,
          y: 1260,
        },
      },
    },
    {
      id: "$$step-placeholder-idle-0Yes-5khqpQ",
      name: "ifElseYes0",
      type: "idle",
      transitions: [
        {
          targetStepId: "$$step-if-condition-idle-1-EumTgn",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 72.5,
          y: 1380,
        },
      },
    },
    {
      id: "$$step-if-condition-idle-1-EumTgn",
      name: "ifElse1",
      type: "idle",
      if: {
        condition: {
          "send_multiple_select-0-8HthCF.userResponse.optionIds": {
            array_contains: "asia-send_multiple_select-0-8HthCF",
          },
        },
        elseStepId: "$$step-if-condition-idle-2-FuzhUx",
      },
      transitions: [
        {
          targetStepId: "$$step-placeholder-idle-1Yes-WGM8ZH",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 0,
          y: 1500,
        },
      },
    },
    {
      id: "$$step-placeholder-idle-1Yes-WGM8ZH",
      name: "ifElseYes1",
      type: "idle",
      transitions: [
        {
          targetStepId: "$$step-if-condition-idle-2-FuzhUx",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 72.5,
          y: 1620,
        },
      },
    },
    {
      id: "$$step-if-condition-idle-2-FuzhUx",
      name: "ifElse2",
      type: "idle",
      if: {
        condition: {
          "send_multiple_select-0-8HthCF.userResponse.optionIds": {
            array_contains: "na-send_multiple_select-0-8HthCF",
          },
        },
        elseStepId: "$$step-placeholder-idle-Done-_c_5f2",
      },
      transitions: [
        {
          targetStepId: "$$step-placeholder-idle-2Yes-4j0pnZ",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 0,
          y: 1740,
        },
      },
    },
    {
      id: "$$step-placeholder-idle-2Yes-4j0pnZ",
      name: "ifElseYes2",
      type: "idle",
      transitions: [
        {
          targetStepId: "$$step-placeholder-idle-Done-_c_5f2",
        },
      ],
      configs: {
        $$editorPosition: {
          x: 72.5,
          y: 1860,
        },
      },
    },
    {
      type: "idle",
      id: "$$step-placeholder-idle-Done-_c_5f2",
      transitions: [],
      configs: {
        $$editorPosition: {
          x: 0,
          y: 1980,
        },
      },
    },
  ];
  return (
    <div className="h-screen m-5">
      <div className="max-w-sm">
        <p>Bug: after deletion, the last placeholder node does not move up.</p>
        To reproduce the bug, delete the second multi-select step.
      </div>
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...privateChannelTrigger,
            steps,
            updatedAt: new Date(),
            isDraft: true,
            liveTriggerConfigs: {
              type: "button_click",
            },
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export function CrashOnDeleteAfterInsertingStepsBetween() {
  const steps = [
    {
      id: "send_message-0-GD6I6-",
      type: "send_message",
      configs: {
        message: "Hello world!",
        channelId: "983800464690806817",
        ephemeral: false,
        removeUrlEmbeds: false,
        $$editorPosition: {
          x: -3.017509727626475,
          y: 385.08132295719844,
        },
        $$editorHtmlMessage: "<p>Hello world!</p>",
      },
      isEntry: false,
      isGoal: false,
      name: "Send a message",
      transitions: [],
    },
    {
      id: "send_message-0-VbBbuD",
      type: "send_message",
      configs: {
        message: "Hello world!",
        channelId: "983800464690806817",
        ephemeral: false,
        removeUrlEmbeds: false,
        $$editorPosition: {
          x: 0,
          y: 180,
        },
        $$editorHtmlMessage: "<p>Hello world!</p>",
        sendBrandingIfNotPremium: true,
      },
      isEntry: true,
      isGoal: false,
      name: "Send a message",
      transitions: [
        {
          targetStepId: "send_message-0-GD6I6-",
        },
      ],
    },
  ];
  return (
    <div className="h-screen m-5">
      <div className="max-w-sm">
        To reproduce the bug (crash), delete the second send_message step.
      </div>
      <TriggerEditorFrontend
        initialValidationErrors={[]}
        error={null}
        initialLiveTriggerId={"1"}
        initialTrigger={
          {
            ...privateChannelTrigger,
            steps,
            updatedAt: new Date(),
            isDraft: true,
            liveTriggerConfigs: {
              type: "button_click",
            },
          } as TriggerForEditor
        }
        onPersist={onPersist}
        isLoadingError={false}
      />
    </div>
  );
}

export default {
  title: "Trigger editor",
};
